import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { sendRequest } from 'api/sendRequest';
import { useQuery as useQueryParams } from 'components/hooks/useQuery';
import { useSelectedView } from 'reducers/viewsSlice';

import { REVIEW_PROVIDER_TYPES } from 'constants/ProjectConstants';
import { endpoints } from 'constants/endpoints';
import { REACT_QUERY_KEYS } from 'constants/reactQueryKeys';

import { useBulkActionContext } from '../BulkActions/BulkActionContext';
import { useBulkNotifyContext } from './BulkNotifyContext';

export interface NotifyOptionType {
  providerType: keyof typeof REVIEW_PROVIDER_TYPES;
  recipientCount: number;
  employeeCount: number;
}

const getBulkNotifyOptionsApi = ({
  employeesList,
  reviewCycleId,
  cohortId,
}): Promise<{ entity: NotifyOptionType[] }> => {
  const url = endpoints.analytics.review.bulkNotify.options;
  return sendRequest({
    url,
    method: 'POST',
    credentials: 'include',
    body: {
      employeesList,
      reviewCycleId,
      cohortId,
    },
  });
};

const submitBulkNotify = ({
  providerTypes,
  employeesList,
  reviewCycleId,
  cohortId,
}: {
  providerTypes: string[];
  employeesList: number[];
  reviewCycleId: number;
  cohortId: number;
}) => {
  const url = endpoints.analytics.review.bulkNotify.notify;
  return sendRequest({
    url,
    method: 'POST',
    credentials: 'include',
    body: {
      providerTypes,
      employeesList,
      reviewCycleId,
      cohortId,
    },
  });
};

export const useGetBulkNotifyOptionsQuery = () => {
  const { selectedEmployeeIds } = useBulkActionContext();
  const { reviewCycleId } = useQueryParams();
  const { id: cohortId } = useSelectedView();

  return useQuery(
    [REACT_QUERY_KEYS.bulkNotify, ...selectedEmployeeIds, reviewCycleId, cohortId],
    () =>
      getBulkNotifyOptionsApi({
        employeesList: selectedEmployeeIds,
        reviewCycleId: +reviewCycleId,
        cohortId: +cohortId,
      }).then((res) => res.entity),
    {
      enabled: selectedEmployeeIds.length > 0 && !!reviewCycleId && !!cohortId,
    }
  );
};

export const useSubmitBulkNotifyMutation = () => {
  const { selectedOptions, setBulkNotifySuccessful, setResultScreen } = useBulkNotifyContext();
  const { selectedEmployeeIds } = useBulkActionContext();

  const { reviewCycleId } = useQueryParams();
  const queryClient = useQueryClient();
  const { id: cohortId } = useSelectedView();

  return useMutation(
    () =>
      submitBulkNotify({
        providerTypes: selectedOptions,
        employeesList: selectedEmployeeIds,
        reviewCycleId: +reviewCycleId,
        cohortId: +cohortId,
      }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([REACT_QUERY_KEYS.reviewsManage]);
        setBulkNotifySuccessful(true);
        setResultScreen();
      },
      onError: () => {
        setBulkNotifySuccessful(false);
        setResultScreen();
      },

      mutationKey: [REACT_QUERY_KEYS.bulkNotify],
    }
  );
};
